import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { InsightBar, statusColor } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { ConvertQuoteModal } from './ConvertQuoteModal.jsx';
import { QuoteCreateModal } from './QuoteCreateModal.jsx';
import { QuoteSendModal } from './QuoteSendModal.jsx';

export function QuotesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const palette = useChartPalette();
  const [createOpen, setCreateOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);

  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['sales', 'quotes'],
    queryFn: () => salesApi.listQuotes(),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const { data: insights } = useQuery({
    queryKey: ['sales', 'quote-insights'],
    queryFn: () => salesApi.quoteInsights().then((r) => r.items),
  });
  const byStatus = insights?.byStatus || [];
  const acceptance =
    insights?.acceptanceRate == null ? '-' : `${Math.round(insights.acceptanceRate * 100)}%`;

  const accept = useMutation({
    mutationFn: (id) => salesApi.acceptQuote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'quotes'] }),
  });
  const reject = useMutation({
    mutationFn: (id) => salesApi.rejectQuote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'quotes'] }),
  });

  const columns = [
    {
      key: 'quoteNumber',
      label: t('col.number'),
      render: (r) => <Link to={`/sales/quotes/${r.id}`}>{r.quoteNumber}</Link>,
    },
    { key: 'quoteDate',   label: t('col.date'),                 render: (r) => formatDate(r.quoteDate) },
    { key: 'validUntil',  label: t('sales.quotes.col.validUntil'), render: (r) => formatDate(r.validUntil) },
    { key: 'totalAmount', label: t('col.total'), align: 'right', render: (r) => formatCurrency(r.totalAmount, r.currency) },
    { key: 'status',      label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    {
      key: 'actions',
      label: '',
      render: (r) => {
        const buttons = [];
        if (r.status === 'DRAFT') {
          buttons.push(
            <Button key="send" size="sm" variant="primary" onClick={() => setSendTarget(r)}>
              {t('sales.quotes.action.send')}
            </Button>,
          );
        }
        // Customer decisions only after sending; converting only once sent/accepted.
        if (r.status === 'SENT') {
          buttons.push(
            <Button key="accept" size="sm" variant="default" disabled={accept.isPending} onClick={() => accept.mutate(r.id)}>
              {t('sales.quotes.action.accept')}
            </Button>,
            <Button key="reject" size="sm" variant="danger" disabled={reject.isPending} onClick={() => reject.mutate(r.id)}>
              {t('sales.quotes.action.reject')}
            </Button>,
          );
        }
        if (r.status === 'ACCEPTED' || r.status === 'SENT') {
          buttons.push(
            <Button key="convert" size="sm" variant={r.status === 'ACCEPTED' ? 'primary' : 'ghost'} onClick={() => setConvertTarget(r)}>
              {t('sales.quotes.action.convert')}
            </Button>,
          );
        }
        return buttons.length ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{buttons}</div> : null;
      },
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('sales.quotes.title')}</h1>
          <p className="muted">{t('sales.quotes.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('sales.quotes.action.new')}
          </Button>
        </div>
      </header>

      {byStatus.length > 0 && (
        <InsightBar
          title={t('sales.quotes.insight.dist')}
          labels={byStatus.map((b) => t(`status.${b.status}`))}
          values={byStatus.map((b) => Number(b.amount))}
          colors={byStatus.map((b) => statusColor(palette, b.status))}
          formatValue={(n) => formatCurrency(n)}
          metrics={[
            { label: t('sales.quotes.insight.open'), value: insights.open.count },
            { label: t('sales.quotes.insight.openValue'), value: formatCurrency(insights.open.amount) },
            { label: t('sales.quotes.insight.acceptance'), value: acceptance },
            { label: t('sales.quotes.insight.expiring'), value: insights.expiringSoon.count },
          ]}
        />
      )}

      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('sales.quotes.empty')} onRowClick={(r) => navigate(`/sales/quotes/${r.id}`)} />
      <QuoteCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ConvertQuoteModal quote={convertTarget} onClose={() => setConvertTarget(null)} />
      <QuoteSendModal quote={sendTarget} onClose={() => setSendTarget(null)} />
    </section>
  );
}
